import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  CardMedia,
  CardContent,
  CardActions,
} from '@mui/material';
import axios from 'axios';
import {
  AccountBalance as BalanceIcon,
  LocalOffer as PointsIcon,
  ArrowUpward as WithdrawIcon,
  CardGiftcard as RewardIcon,
  FileDownload as DownloadIcon
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// Derive server base (strip any trailing /api) so we can resolve static files
const SERVER_BASE = API_BASE.includes('/api') ? API_BASE.substring(0, API_BASE.indexOf('/api')) : API_BASE;

const MINIMUM_WITHDRAWAL = 20; // Минимальная сумма вывода в BYN

export default function Balance() {
  const [currentTab, setCurrentTab] = useState('balance');
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [withdrawalMethod, setWithdrawalMethod] = useState('bank');
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [bankDetails, setBankDetails] = useState({
    cardNumber: '',
    cardHolder: ''
  });
  const [processing, setProcessing] = useState(false);
  const [balance, setBalance] = useState({ money: 0, points: 0 });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [withdrawals, setWithdrawals] = useState([]);
  const [convertDialog, setConvertDialog] = useState(false);
  const [convertAmount, setConvertAmount] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [balRes, txRes, wdRes] = await Promise.all([
          axios.get(`${API_BASE}/rewards/balance`, { headers }),
          axios.get(`${API_BASE}/rewards/transactions`, { headers }),
          axios.get(`${API_BASE}/rewards/withdrawals`, { headers })
        ]);
        setBalance(balRes.data || { money: 0, points: 0 });
        setTransactions(txRes.data || []);
        setWithdrawals(wdRes.data || []);
        setError(null);
      } catch (e) {
        console.error('Не удалось загрузить данные баланса', e);
        setError('Не удалось загрузить данные баланса');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleWithdraw = async () => {
    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Проверка минимальной суммы вывода
      const amount = Number(withdrawalAmount);
      if (!amount || amount < MINIMUM_WITHDRAWAL) {
        setError(`Минимальная сумма вывода: ${MINIMUM_WITHDRAWAL} BYN`);
        setProcessing(false);
        return;
      }
      const details = withdrawalMethod === 'mobile'
        ? { mobileNumber }
        : { cardNumber: bankDetails.cardNumber, cardHolder: bankDetails.cardHolder };
      await axios.post(`${API_BASE}/rewards/withdraw`, {
        amount: Number(withdrawalAmount),
        method: withdrawalMethod,
        details
      }, { headers });
      setWithdrawDialog(false);
      // Refresh data
      const balRes = await axios.get(`${API_BASE}/rewards/balance`, { headers });
      const txRes = await axios.get(`${API_BASE}/rewards/transactions`, { headers });
      setBalance(balRes.data || { money: 0, points: 0 });
      setTransactions(txRes.data || []);
    } catch (e) {
      console.error('Withdrawal failed', e);
      setError(e.response?.data?.error || 'Не удалось отправить запрос на выплату');
    } finally {
      setProcessing(false);
    }
  };

  const handleConvert = async () => {
    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const value = Math.round(Number(convertAmount) * 10) / 10; // Round to 1 decimal place
      if (!Number.isFinite(value) || value < 0.1) {
        setError('Укажите корректную сумму для конвертации (минимум 0.1 BYN)');
        setProcessing(false);
        return;
      }
      await axios.post(`${API_BASE}/rewards/convert-to-points`, { amount: value }, { headers });
      setConvertDialog(false);
      setConvertAmount('');
      // Refresh balance and transactions
      const [balRes, txRes] = await Promise.all([
        axios.get(`${API_BASE}/rewards/balance`, { headers }),
        axios.get(`${API_BASE}/rewards/transactions`, { headers })
      ]);
      setBalance(balRes.data || { money: 0, points: 0 });
      setTransactions(txRes.data || []);
      setError(null);
    } catch (e) {
      console.error('Conversion failed', e);
      setError(e.response?.data?.error || 'Не удалось выполнить конвертацию');
    } finally {
      setProcessing(false);
    }
  };



  const handleDownloadContract = () => {
    // Open the contract file in a new tab; browser will download it
    window.open(`${API_BASE}/rewards/contract`, '_blank');
  };

  const renderBalanceSection = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
            borderRadius: '24px',
            border: '1px solid rgba(116, 185, 255, 0.5)',
            boxShadow: '0 8px 32px rgba(9, 132, 227, 0.3)',
          }}
        >
          <BalanceIcon sx={{ fontSize: 48, color: '#fff', mb: 2 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>
            {Number(balance.money || 0).toFixed(2)} BYN
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 3, fontWeight: 500 }}>
            Доступный баланс
          </Typography>
          <Button
            variant="contained"
            startIcon={<WithdrawIcon />}
            onClick={() => setWithdrawDialog(true)}
            disabled={Number(balance.money || 0) < MINIMUM_WITHDRAWAL}
            sx={{
              mt: 1,
              px: 3,
              py: 1.5,
              borderRadius: '16px',
              background: '#fff',
              color: '#0984e3',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(255, 255, 255, 0.3)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.9)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(255, 255, 255, 0.4)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.6)',
              },
              transition: 'all 0.2s'
            }}
          >
            Вывести средства
          </Button>
          <Button
            variant="outlined"
            onClick={() => setConvertDialog(true)}
            disabled={Number(balance.money || 0) <= 0}
            sx={{
              mt: 1,
              ml: 1,
              px: 3,
              py: 1.5,
              borderRadius: '16px',
              border: '2px solid #fff',
              color: '#fff',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                border: '2px solid #fff',
                background: 'rgba(255, 255, 255, 0.1)',
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                border: '2px solid rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
              transition: 'all 0.2s'
            }}
          >
            Конвертировать в баллы
          </Button>
          {Number(balance.money || 0) < MINIMUM_WITHDRAWAL && (
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block', mt: 2, fontWeight: 500 }}>
              Минимальная сумма вывода: {MINIMUM_WITHDRAWAL} BYN
            </Typography>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 234, 167, 0.5)',
            boxShadow: '0 4px 16px rgba(253, 203, 110, 0.3)',
          }}
        >
          <PointsIcon sx={{ fontSize: 48, color: '#744210', mb: 2 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#2d3748', mb: 1 }}>
            {Number(balance.points || 0).toFixed(1)}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#744210', fontWeight: 500 }}>
            Баллы
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderTransactionsSection = () => (
    <>
      {withdrawals.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2,
              fontWeight: 700,
              color: '#2d3748'
            }}
          >
            Заявки на вывод средств
          </Typography>
          <TableContainer 
            component={Paper}
            elevation={0}
            sx={{ 
              borderRadius: '24px',
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Дата</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Сумма</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Метод</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Статус</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Детали</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withdrawals.map((wd) => (
                  <TableRow 
                    key={wd.id}
                    sx={{
                      '&:hover': {
                        background: 'rgba(116, 185, 255, 0.05)',
                      },
                      transition: 'background 0.2s'
                    }}
                  >
                    <TableCell sx={{ color: '#718096' }}>
                      {new Date(wd.createdAt).toLocaleDateString('ru-RU')}
                    </TableCell>
                    <TableCell sx={{ color: '#2d3748', fontWeight: 600 }}>
                      {wd.amount} BYN
                    </TableCell>
                    <TableCell sx={{ color: '#2d3748', fontWeight: 500 }}>
                      {wd.method === 'bank' ? 'Банк' : 'Мобильный'}
                    </TableCell>
                    <TableCell>
                      <Box 
                        sx={{
                          display: 'inline-block',
                          px: 2,
                          py: 0.5,
                          borderRadius: '12px',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          ...(wd.status === 'completed' && {
                            background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                            color: '#fff',
                          }),
                          ...(wd.status === 'rejected' && {
                            background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
                            color: '#fff',
                          }),
                          ...(wd.status === 'pending' && {
                            background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                            color: '#744210',
                          })
                        }}
                      >
                        {{
                          pending: 'На проверке',
                          completed: 'Одобрено',
                          rejected: 'Отклонено'
                        }[wd.status] || wd.status}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {wd.status === 'completed' && (
                        <Box 
                          sx={{
                            display: 'inline-block',
                            px: 2,
                            py: 0.5,
                            borderRadius: '12px',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                            color: '#fff',
                          }}
                        >
                          Выплата одобрена
                        </Box>
                      )}
                      {wd.status === 'rejected' && wd.rejectionComment && (
                        <Typography variant="caption" sx={{ color: '#d63031', fontWeight: 500 }}>
                          {wd.rejectionComment}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 2,
          fontWeight: 700,
          color: '#2d3748'
        }}
      >
        История транзакций
      </Typography>
      <TableContainer 
        component={Paper}
        elevation={0}
        sx={{ 
          borderRadius: '24px',
          overflow: 'hidden',
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)' }}>
              <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Дата</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Описание</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Тип</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Сумма</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Статус</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => (
              <TableRow 
                key={transaction.id}
                sx={{
                  '&:hover': {
                    background: 'rgba(116, 185, 255, 0.05)',
                  },
                  transition: 'background 0.2s'
                }}
              >
                <TableCell sx={{ color: '#718096' }}>
                  {new Date(transaction.createdAt || transaction.date).toLocaleDateString()}
                </TableCell>
                <TableCell sx={{ color: '#2d3748', fontWeight: 500 }}>
                  {transaction.description}
                </TableCell>
                <TableCell sx={{ color: '#718096' }}>
                  {({
                    withdrawal: 'Вывод',
                    reward_claim: 'Награда',
                    refund: 'Возврат',
                    survey_reward: 'Вознаграждение',
                    survey_completion: 'Прохождение опроса',
                    reward_refund: 'Возврат награды',
                    reversal: 'Отмена транзакции',
                    conversion_debit: 'Конвертация (списание)',
                    conversion_credit: 'Конвертация (начисление)',
                    profile_survey_reward: 'Профильный опрос'
                  }[transaction.type]) || transaction.type}
                </TableCell>
                <TableCell align="right">
                  <Box 
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 0.5,
                      borderRadius: '12px',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      ...(transaction.amount > 0 && {
                        background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                        color: '#fff',
                      }),
                      ...(transaction.amount < 0 && {
                        background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
                        color: '#fff',
                      })
                    }}
                  >
                    {transaction.amount > 0 ? '+' : ''}
                    {transaction.currency === 'points' 
                      ? `${Math.abs(transaction.amount).toFixed(1)} баллов`
                      : `${Math.abs(Number(transaction.amount)).toFixed(2)} BYN`}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box 
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 0.5,
                      borderRadius: '12px',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      ...(transaction.status === 'completed' && {
                        background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                        color: '#fff',
                      }),
                      ...(transaction.status === 'rejected' && {
                        background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
                        color: '#fff',
                      }),
                      ...(transaction.status === 'processing' && {
                        background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                        color: '#744210',
                      })
                    }}
                  >
                    {({ completed: 'Завершено', processing: 'В обработке', rejected: 'Отклонено' }[transaction.status]) || transaction.status}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );



  return (
    <Box>
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 1.5, md: 4 },
          mx: { xs: 0, md: 3 },
          mt: 0,
          mb: 2,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '32px',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{ 
            mb: 3,
            '& .MuiTab-root': {
              fontWeight: 600,
              borderRadius: '16px',
              textTransform: 'none',
              fontSize: '0.95rem',
              color: '#718096',
              '&.Mui-selected': {
                color: '#0984e3',
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px',
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
            }
          }}
        >
          <Tab label="Баланс" value="balance" />
          <Tab label="История операций" value="transactions" />
        </Tabs>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              borderRadius: '20px',
              background: 'rgba(255, 118, 117, 0.08)',
              border: '1px solid rgba(255, 118, 117, 0.3)',
            }}
          >
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {currentTab === 'balance' && renderBalanceSection()}
            {currentTab === 'transactions' && renderTransactionsSection()}
          </>
        )}
      </Paper>

      {/* Withdrawal Dialog */}
      <Dialog
        open={withdrawDialog}
        onClose={() => setWithdrawDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '32px',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          fontSize: '1.5rem',
          color: '#2d3748',
          pb: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
        }}>
          Вывод средств
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Доступно: {Number(balance.money || 0).toFixed(2)} BYN
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Минимальная сумма вывода: {MINIMUM_WITHDRAWAL} BYN
            </Typography>
          </Box>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Способ вывода</InputLabel>
            <Select
              value={withdrawalMethod}
              label="Способ вывода"
              onChange={(e) => setWithdrawalMethod(e.target.value)}
            >
              <MenuItem value="mobile">Пополнение мобильного</MenuItem>
              <MenuItem value="bank">Банковская карта</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Сумма"
            type="number"
            value={withdrawalAmount}
            onChange={(e) => setWithdrawalAmount(e.target.value)}
            sx={{ mb: 3 }}
          />

          {withdrawalMethod === 'mobile' ? (
            <TextField
              fullWidth
              label="Номер телефона"
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value)}
              placeholder="+375..."
            />
          ) : (
            <Box>
              <TextField
                fullWidth
                label="Номер карты"
                value={bankDetails.cardNumber}
                onChange={(e) => setBankDetails({...bankDetails, cardNumber: e.target.value})}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Имя владельца карты"
                value={bankDetails.cardHolder}
                onChange={(e) => setBankDetails({...bankDetails, cardHolder: e.target.value})}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setWithdrawDialog(false)}
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: '16px',
              color: '#718096',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.05)',
              }
            }}
          >
            Отмена
          </Button>
          <Button 
            onClick={handleWithdraw}
            disabled={processing || !withdrawalAmount}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              color: '#fff',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(9, 132, 227, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0984e3 0%, #0652DD 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(9, 132, 227, 0.4)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
              transition: 'all 0.2s'
            }}
          >
            {processing ? <CircularProgress size={24} /> : 'Отправить запрос'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog
        open={convertDialog}
        onClose={() => setConvertDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '32px',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          fontSize: '1.5rem',
          color: '#2d3748',
          pb: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
        }}>
          Конвертация BYN в баллы
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            1 BYN = 1 балл. Доступно к конвертации: {Number(balance.money || 0).toFixed(2)} BYN
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            <b style={{ color: '#d32f2f' }}>Внимание:</b> <span style={{ color: '#d32f2f' }}>Обратная конвертация баллов в BYN невозможна!</span>
          </Alert>
          <TextField
            fullWidth
            label="Сумма BYN"
            type="number"
            value={convertAmount}
            onChange={(e) => setConvertAmount(e.target.value)}
            inputProps={{ min: 0.1, step: 0.1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setConvertDialog(false)}
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: '16px',
              color: '#718096',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.05)',
              }
            }}
          >
            Отмена
          </Button>
          <Button
            onClick={handleConvert}
            disabled={processing || !convertAmount}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              color: '#fff',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(9, 132, 227, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0984e3 0%, #0652DD 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(9, 132, 227, 0.4)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
              transition: 'all 0.2s'
            }}
          >
            {processing ? <CircularProgress size={24} /> : 'Конвертировать'}
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
}